import React from 'react';
import { Plus, Minus } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { AddToCartButtonProps } from './add-to-cart-button.types';
import styles from './add-to-cart-button.module.css';
import Button from '../../atoms/button';
import Text from '../../atoms/text';
import Divider from '../../atoms/divider';

/**
 * AddToCartButton - E-commerce cart button
 *
 * A versatile button for adding products to cart with multiple variants.
 * Supports quick add, full display with buy now, and counter mode.
 *
 * ### Key Features
 * - **3 Variants**: Quick icon, full with price, counter
 * - **Loading States**: Separate for add/buy actions
 * - **Cart Badge**: Shows current quantity
 * - **Error Display**: Shows validation messages
 */
const AddToCartButton: React.FC<AddToCartButtonProps> = ({
    variant = 'quick',
    cartQty = 0,
    error,
    onIncrement,
    onDecrement,
    className,
    testID = 'add-to-cart-button',
    callNowButtonProps,
    addToCartButtonProps,
    // buyNowButtonProps,
}) => {
    // Quick variant - Icon with badge
    if (variant === 'quick') {
        return (
            <div className={cn('relative', className)}>
                <Button
                    data-testid={testID}
                    {...addToCartButtonProps}
                    className={styles.buttonQuick}
                    contentClassName={cn(styles.quickVariant, addToCartButtonProps?.className)}
                ></Button>

                {cartQty > 0 && !addToCartButtonProps?.loading && (
                    <span className={styles.label} data-testid={`${testID}-badge`}>
                        {cartQty}
                    </span>
                )}
            </div>
        );
    }

    // Counter variant
    if (variant === 'counter') {
        return (
            <div className={cn('flex items-center gap-2', className)} data-testid={testID}>
                <button
                    onClick={onDecrement}
                    className={cn(
                        'w-10 h-10 rounded-full border-2 border-primary-100',
                        'flex items-center justify-center',
                        'hover:bg-primary-10 active:bg-primary-20',
                        'transition-colors',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                    )}
                    data-testid={`${testID}-decrement`}
                >
                    <Minus className="w-5 h-5 text-primary-100" />
                </button>

                <span
                    className="min-w-[40px] text-center font-bold text-lg text-neutral-100"
                    data-testid={`${testID}-quantity`}
                >
                    {cartQty}
                </span>

                <button
                    onClick={onIncrement}
                    className={cn(
                        'w-10 h-10 rounded-full',
                        'bg-primary-100 hover:bg-primary-90 active:bg-primary-110',
                        'flex items-center justify-center',
                        'transition-colors',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                    )}
                    data-testid={`${testID}-increment`}
                >
                    <Plus className="w-5 h-5 text-[var(--color-neutral-0)]" />
                </button>
            </div>
        );
    }

    // Full variant
    return (
        <div className={cn(styles.fullVariantContainer, className)} data-testid={testID}>
            {error && (
                <Text className={styles.errorMessage} data-testid={`${testID}-error`}>
                    {error}
                </Text>
            )}

            <div className={styles.fullVariant}>
                {/* <div className={styles.fullVariantLeft}></div> */}
                <Button
                    {...callNowButtonProps}
                    showIf={!!callNowButtonProps?.onClick}
                    className={cn(styles.fullVariantLeft, callNowButtonProps)}
                ></Button>

                <Divider orientation="vertical" />

                <Button
                    {...addToCartButtonProps}
                    showIf={!!addToCartButtonProps?.onClick}
                    className={cn(styles.fullVariantRight, addToCartButtonProps)}
                ></Button>

                {/* <div className={styles.fullVariantRight}>
                    <Button
                        {...buyNowButtonProps}
                        showIf={!!buyNowButtonProps?.onClick}
                        className={cn(styles.actionButton, buyNowButtonProps)}
                    ></Button>
                </div> */}
            </div>
        </div>
    );
};

export default AddToCartButton;
